import React from 'react';

/** Table qui sait défiler à une position (rsuite-table, par exemple). */
interface TableDefilable {
	scrollTop: (top: number) => void;
}

interface DefilementConserveOptions {
	/** Position retrouvée à l'affichage (gardée par l'appelant, dans un store par exemple). */
	positionInitiale: number;
	/** Lignes affichées : la position n'est restaurée qu'une fois, à ce moment-là. */
	isPret: boolean;
	/** Change quand la table est recréée (changement de filtre) : elle repart alors en haut. */
	cleTable: unknown;
	/** Enregistre la position au démontage (retour vers une fiche). */
	enregistrer: (position: number) => void;
}

/**
 * Position de défilement d'une table conservée d'un affichage à l'autre : suivie sans re-rendu, restaurée une fois
 * les lignes affichées, remise à zéro quand la table est recréée, enregistrée au démontage.
 *
 * @returns la ref à poser sur la table et le gestionnaire de défilement à lui passer (onScroll)
 */
const useDefilementConserve = <T extends TableDefilable>({
	positionInitiale,
	isPret,
	cleTable,
	enregistrer,
}: DefilementConserveOptions) => {
	const tableRef = React.useRef<T>(null);
	const position = React.useRef(positionInitiale);
	const isRestauree = React.useRef(false);
	const enregistrerRef = React.useRef(enregistrer);
	enregistrerRef.current = enregistrer;

	const onScroll = React.useCallback((_scrollX: number, scrollY: number) => {
		position.current = scrollY;
	}, []);

	React.useEffect(() => {
		if (isRestauree.current || !isPret) return;
		isRestauree.current = true;
		if (position.current > 0) {
			tableRef.current?.scrollTop(position.current);
		}
	}, [isPret]);

	const cleTablePrecedente = React.useRef(cleTable);
	React.useEffect(() => {
		if (cleTablePrecedente.current !== cleTable) {
			cleTablePrecedente.current = cleTable;
			position.current = 0;
		}
	}, [cleTable]);

	React.useEffect(
		() => () => {
			enregistrerRef.current(position.current);
		},
		[],
	);

	return { tableRef, onScroll };
};

export default useDefilementConserve;
